import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Select,
    MenuItem,
    Chip,
    FormControlLabel,
    Checkbox,
    Stack,
    IconButton,
    TextField,
    CircularProgress,
    Switch,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { QRCodeCanvas } from 'qrcode.react';
import { db } from '../instant';
import { id } from '@instantdb/react';
import { processEventSelection } from '../utils/eventSelection';

/** Invite code validity options: value in ms, label for UI */
const VALIDITY_OPTIONS: { valueMs: number; label: string }[] = [
    { valueMs: 60 * 60 * 1000, label: '1 hour' },
    { valueMs: 2 * 60 * 60 * 1000, label: '2 hours' },
    { valueMs: 6 * 60 * 60 * 1000, label: '6 hours' },
    { valueMs: 24 * 60 * 60 * 1000, label: '24 hours' },
    { valueMs: 7 * 24 * 60 * 60 * 1000, label: '7 days' },
];

const DEFAULT_VALIDITY_MS = 2 * 60 * 60 * 1000; // 2 hours

interface Event {
    id: string;
    name: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    events: Event[];
    permissionLabels: Record<string, string>;
    preSelectedEvent?: Event;
}

export default function GenerateQRDialog(props: Props) {
    const { open, onClose, events, permissionLabels, preSelectedEvent } = props;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [userMode, setUserMode] = useState<'single' | 'multi'>('multi');
    const [selectedEventIds, setSelectedEventIds] = useState<string[]>(preSelectedEvent ? [preSelectedEvent.id] : []);
    const [selectedPermissions, setSelectedPermissions] = useState<Record<string, boolean>>({
        admin: false,
        editMapStationsNotes: false,
        createEditEventInfo: false,
        viewWasteData: false,
        editWasteData: false,
        generateReports: false,
        editTodos: false,
        completeTodos: false,
        viewInventory: false,
        manageInventory: false,
        editUsers: false,
    });
    const [email, setEmail] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
    const [showQRModal, setShowQRModal] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string>('');
    const [linkCopied, setLinkCopied] = useState(false);
    const [eventSelectOpen, setEventSelectOpen] = useState(false);
    const [mobileEventPickerOpen, setMobileEventPickerOpen] = useState(false);
    const [validityMs, setValidityMs] = useState(DEFAULT_VALIDITY_MS);
    const [qrExpiresAt, setQrExpiresAt] = useState<Date | null>(null);

    // Reset form when dialog opens
    useEffect(() => {
        if (open) {
            setUserMode('multi');
            setSelectedEventIds(preSelectedEvent ? [preSelectedEvent.id] : []);
            setSelectedPermissions({
                admin: false,
                editMapStationsNotes: false,
                createEditEventInfo: false,
                viewWasteData: false,
                editWasteData: false,
                generateReports: false,
                editTodos: false,
                completeTodos: false,
                viewInventory: false,
                manageInventory: false,
                editUsers: false,
            });
            setEmail('');
            setName('');
            setQrCodeUrl('');
            setShowQRModal(false);
            setError('');
            setLinkCopied(false);
            setEventSelectOpen(false);
            setMobileEventPickerOpen(false);
            setValidityMs(DEFAULT_VALIDITY_MS);
            setQrExpiresAt(null);
        }
    }, [open, preSelectedEvent]);

    const handleEventChange = (value: string[]) => {
        const newValues = Array.isArray(value) ? value : [value];
        const result = processEventSelection(newValues, selectedEventIds);
        setSelectedEventIds(result);
        if (result.includes('all')) {
            setEventSelectOpen(false);
            setMobileEventPickerOpen(false);
        }
    };

    /** Mobile list: toggle "All Events" (mirrors Select `value="all"` behavior). */
    const handleMobileToggleAll = () => {
        if (selectedEventIds.includes('all')) {
            handleEventChange([]);
        } else {
            handleEventChange(['all']);
        }
    };

    /**
     * Mobile list: toggle one event. When "all" is active, MUI Select transitions via both
     * `all` and the specific id in the value array — `processEventSelection` then keeps only that id.
     */
    const handleMobileToggleEvent = (eventId: string) => {
        if (selectedEventIds.includes('all')) {
            handleEventChange(['all', eventId]);
            return;
        }
        const next = selectedEventIds.includes(eventId) ? selectedEventIds.filter(id => id !== eventId) : [...selectedEventIds, eventId];
        handleEventChange(next);
    };

    const closeMobileEventPicker = () => setMobileEventPickerOpen(false);

    const renderEventChips = (selected: string[]) => (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                justifyContent: 'flex-start',
            }}
        >
            {selected.map(value => (
                <Chip key={value} label={value === 'all' ? 'All Events' : events.find(e => e.id === value)?.name || value} size="small" />
            ))}
        </Box>
    );

    const sortedEvents = [...events].sort((a, b) => a.name.localeCompare(b.name));

    const handlePermissionChange = (key: string, value: boolean) => {
        setSelectedPermissions(prev => {
            const updated = { ...prev, [key]: value };

            // If toggling admin, toggle all other permissions
            if (key === 'admin') {
                Object.keys(permissionLabels).forEach(permKey => {
                    if (permKey !== 'admin') {
                        updated[permKey] = value;
                    }
                });
            } else {
                // Check if all permissions (except admin) are now true
                const allNonAdminTrue = Object.keys(permissionLabels)
                    .filter(permKey => permKey !== 'admin')
                    .every(permKey => {
                        if (permKey === key) return value;
                        return updated[permKey] === true;
                    });

                // If all permissions are true, set admin to true
                // If any permission is false and admin is true, set admin to false
                if (allNonAdminTrue) {
                    updated.admin = true;
                } else if (updated.admin === true && value === false) {
                    updated.admin = false;
                }
            }

            return updated;
        });
    };

    const generateRandomCode = (): string => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
        let result = '';
        for (let i = 0; i < 10; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    };

    const generateSignupUrl = async () => {
        setIsGenerating(true);
        setError('');
        try {
            // Generate a random signup code
            const code = generateRandomCode();
            const signupCodeId = id();
            const expiresAt = new Date(Date.now() + validityMs);

            // Prepare eventIds string
            const eventIdsStr = selectedEventIds.includes('all') ? 'all' : selectedEventIds.join(',');

            // Create the signup code in the database (signupCodes entity, alias column)
            await db.transact(
                db.tx.signupCodes[signupCodeId].update({
                    alias: code,
                    permissions: selectedPermissions,
                    eventIds: eventIdsStr,
                    email: userMode === 'single' ? email : null,
                    name: userMode === 'single' ? name : null,
                    expiresAt,
                    used: false,
                })
            );

            // Generate the signup URL
            const url = `${window.location.origin}/signup?code=${code}`;
            setQrCodeUrl(url);
            setQrExpiresAt(expiresAt);
            return url;
        } catch (err) {
            console.error('Error generating signup URL:', err);
            setError('Error generating signup URL: ' + (err instanceof Error ? err.message : String(err)));
            throw err;
        } finally {
            setIsGenerating(false);
        }
    };

    const handleShowQR = async () => {
        await generateSignupUrl();
        setShowQRModal(true);
    };

    const handleCopyLink = async () => {
        try {
            const url = qrCodeUrl || (await generateSignupUrl());
            navigator.clipboard.writeText(url);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 3000);
        } catch (err) {
            console.log(err);
        }
    };

    const handleDownloadQR = () => {
        const canvas = document.querySelector('canvas');
        if (canvas) {
            const url = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.download = 'signup-qr-code.png';
            link.href = url;
            link.click();
        }
    };

    const isFormValid = () => {
        return selectedEventIds.length > 0;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={600}>
                        Invite User Code
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {error && (
                    <Box sx={{ p: 1, bgcolor: '#ffebee', borderRadius: 1, borderLeft: '4px solid #f44336' }}>
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    </Box>
                )}

                {/* User Mode Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                    <Typography variant="body2" fontWeight={500}>
                        Multi
                    </Typography>
                    <Switch
                        checked={userMode === 'single'}
                        onChange={e => setUserMode(e.target.checked ? 'single' : 'multi')}
                        size="small"
                    />
                    <Typography variant="body2" fontWeight={500}>
                        Single
                    </Typography>
                </Box>

                {/* Link validity */}
                <Box>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="invite-validity-label">Valid for</InputLabel>
                        <Select
                            labelId="invite-validity-label"
                            value={validityMs}
                            label="Valid for"
                            onChange={e => setValidityMs(Number(e.target.value))}
                        >
                            {VALIDITY_OPTIONS.map(({ valueMs, label }) => (
                                <MenuItem key={valueMs} value={valueMs}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Invite link will expire after this time
                    </Typography>
                </Box>

                {/* User Info (Only for Single User Mode) */}
                {userMode === 'single' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="email"
                            label="Email"
                            placeholder="user@example.com"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Name"
                            placeholder="John Doe"
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                    </Box>
                )}

                {/* Events Selection */}
                <Box sx={{ flexShrink: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Event
                    </Typography>
                    {preSelectedEvent ? (
                        <Typography variant="body2" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            {preSelectedEvent.name}
                        </Typography>
                    ) : isMobile ? (
                        <>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => setMobileEventPickerOpen(true)}
                                sx={{
                                    py: 1,
                                    minHeight: 48,
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                    textTransform: 'none',
                                }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    {selectedEventIds.length > 0 ? (
                                        renderEventChips(selectedEventIds)
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Choose events…
                                        </Typography>
                                    )}
                                </Box>
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Tap to select events. Close with the X button on the next screen.
                            </Typography>
                            <Dialog fullScreen open={mobileEventPickerOpen} onClose={closeMobileEventPicker}>
                                <DialogTitle sx={{ py: 1.5 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                        <Typography variant="h6" fontWeight={600} component="span">
                                            Events
                                        </Typography>
                                        <IconButton
                                            onClick={closeMobileEventPicker}
                                            size="small"
                                            edge="end"
                                            aria-label="Close event picker"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                </DialogTitle>
                                <DialogContent sx={{ px: 0, pt: 0 }}>
                                    <List disablePadding dense>
                                        <ListItem disablePadding>
                                            <ListItemButton onClick={handleMobileToggleAll}>
                                                <Checkbox
                                                    edge="start"
                                                    tabIndex={-1}
                                                    checked={selectedEventIds.includes('all')}
                                                    disableRipple
                                                    inputProps={{ 'aria-labelledby': 'invite-events-all-label' }}
                                                />
                                                <ListItemText id="invite-events-all-label" primary="All Events" />
                                            </ListItemButton>
                                        </ListItem>
                                        {sortedEvents.map(event => (
                                            <ListItem key={event.id} disablePadding>
                                                <ListItemButton onClick={() => handleMobileToggleEvent(event.id)}>
                                                    <Checkbox
                                                        edge="start"
                                                        tabIndex={-1}
                                                        checked={!selectedEventIds.includes('all') && selectedEventIds.includes(event.id)}
                                                        disableRipple
                                                        inputProps={{
                                                            'aria-labelledby': `invite-events-${event.id}-label`,
                                                        }}
                                                    />
                                                    <ListItemText id={`invite-events-${event.id}-label`} primary={event.name} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </DialogContent>
                            </Dialog>
                        </>
                    ) : (
                        <Select
                            multiple
                            value={selectedEventIds}
                            open={eventSelectOpen}
                            onOpen={() => setEventSelectOpen(true)}
                            onClose={() => setEventSelectOpen(false)}
                            onChange={e => {
                                const value = e.target.value;
                                handleEventChange(Array.isArray(value) ? value : [value]);
                            }}
                            renderValue={selected => renderEventChips(selected)}
                            sx={{ width: '100%' }}
                        >
                            <MenuItem value="all">All Events</MenuItem>
                            {sortedEvents.map(event => (
                                <MenuItem key={event.id} value={event.id}>
                                    {event.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </Box>

                {/* Permissions Selection - Scrollable */}
                <Box sx={{ flexShrink: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Permissions
                    </Typography>
                    <Box
                        sx={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            border: '1px solid rgba(0,0,0,0.12)',
                            borderRadius: '4px',
                            p: 1,
                        }}
                    >
                        <Stack spacing={1}>
                            {Object.entries(permissionLabels).map(([key, label]) => (
                                <FormControlLabel
                                    key={key}
                                    control={
                                        <Checkbox
                                            checked={selectedPermissions[key] || false}
                                            onChange={e => handlePermissionChange(key, e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={label}
                                />
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleShowQR} variant="outlined" disabled={isGenerating || !isFormValid()}>
                    {isGenerating ? <CircularProgress size={24} /> : 'Show QR'}
                </Button>
                <Button
                    onClick={handleCopyLink}
                    variant="contained"
                    disabled={isGenerating || !isFormValid()}
                    endIcon={linkCopied ? <CheckIcon /> : undefined}
                >
                    Copy Link
                </Button>
            </DialogActions>

            {/* QR Code Modal */}
            <Dialog open={showQRModal} onClose={() => setShowQRModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight={600}>
                            QR Code
                        </Typography>
                        <IconButton onClick={() => setShowQRModal(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        pt: 4,
                        pb: 1.5,
                        gap: 2,
                    }}
                >
                    {qrCodeUrl && <QRCodeCanvas value={qrCodeUrl} size={300} level="H" includeMargin={true} />}
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                        {qrCodeUrl}
                    </Typography>
                    {qrExpiresAt && (
                        <Typography variant="body2" color="text.secondary">
                            Expires {qrExpiresAt.toLocaleString()}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleDownloadQR} variant="contained" fullWidth>
                        Download QR
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
